import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Upload, X } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";

const MAX_FILES = 30;
const MAX_MB = 5;
const ALLOWED_MIMES = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"]);

/** Strip extension + non-alphanumerics + lowercase. */
const normalize = (s) => String(s || "").toLowerCase().replace(/\.[^.]+$/, "").replace(/[^a-z0-9]/g, "");

/**
 * Match a file's normalized name to the best partner_shop entry.
 * Precedence:
 *   1. Exact match on normalized slug
 *   2. Exact match on normalized brand_name
 *   3. `contains` in either direction — filename contains normalized brand OR vice versa
 * When multiple contain-matches qualify, prefer the SHORTEST brand slug (most specific).
 *
 * Returns { match: shop | null, candidates: shop[] } where `candidates` is populated only
 * for the ambiguous contains case (2+ equally-good contains matches) so the caller can
 * render a picker.
 */
function matchBrand(fileName, shops) {
    const key = normalize(fileName);
    if (!key) return { match: null, candidates: [] };

    // 1. exact normalized slug
    const bySlug = shops.filter((s) => normalize(s.slug) === key);
    if (bySlug.length === 1) return { match: bySlug[0], candidates: [] };
    if (bySlug.length > 1) return { match: null, candidates: bySlug };

    // 2. exact normalized brand_name
    const byName = shops.filter((s) => normalize(s.brand_name) === key);
    if (byName.length === 1) return { match: byName[0], candidates: [] };
    if (byName.length > 1) return { match: null, candidates: byName };

    // 3. bi-directional contains
    const contains = shops.filter((s) => {
        const nSlug = normalize(s.slug);
        const nName = normalize(s.brand_name);
        return (
            (nSlug && (key.includes(nSlug) || nSlug.includes(key))) ||
            (nName && (key.includes(nName) || nName.includes(key)))
        );
    });
    if (contains.length === 0) return { match: null, candidates: [] };
    if (contains.length === 1) return { match: contains[0], candidates: [] };

    // Prefer shortest slug (most specific) among contains-matches. If a unique
    // shortest exists, treat as match; else ambiguous.
    const sorted = [...contains].sort((a, b) => normalize(a.slug).length - normalize(b.slug).length);
    const shortest = normalize(sorted[0].slug).length;
    const shortestSet = sorted.filter((s) => normalize(s.slug).length === shortest);
    if (shortestSet.length === 1) return { match: shortestSet[0], candidates: [] };
    return { match: null, candidates: shortestSet };
}

/**
 * Bulk logo uploader.
 *
 *   props:
 *     open       — controlled visibility
 *     onOpenChange
 *     shops      — the full brands list already loaded on the page
 *     onDone     — called after the batch finishes (successfully or not) so the page can `load()`
 */
export default function BulkLogoUploadDialog({ open, onOpenChange, shops = [], onDone }) {
    // Rows: { id, file, previewUrl, matchId, ambiguous, candidates, size, error, status, message }
    // status: 'idle' | 'uploading' | 'done' | 'failed'
    const [rows, setRows] = useState([]);
    const [running, setRunning] = useState(false);
    const [summary, setSummary] = useState(null);
    const inputRef = useRef(null);

    const shopsById = useMemo(() => {
        const m = new Map();
        shops.forEach((s) => m.set(s.id, s));
        return m;
    }, [shops]);

    const matchedCount = useMemo(
        () => rows.filter((r) => r.matchId && !r.error).length,
        [rows],
    );

    const validateFile = (file) => {
        if (!ALLOWED_MIMES.has(file.type)) return `Unsupported type "${file.type || "unknown"}". PNG / JPG / WEBP only.`;
        if (file.size > MAX_MB * 1024 * 1024) return `Too large (${(file.size / 1048576).toFixed(1)} MB). Max ${MAX_MB} MB.`;
        return null;
    };

    const addFiles = (fileList) => {
        setSummary(null);
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        const slots = MAX_FILES - rows.length;
        if (slots <= 0) {
            toast.error(`Max ${MAX_FILES} files per batch — remove some first.`);
            return;
        }
        const accepted = incoming.slice(0, slots);
        if (incoming.length > slots) toast.error(`Only ${slots} slot(s) left — added first ${slots}.`);

        const built = accepted.map((f) => {
            const err = validateFile(f);
            const { match, candidates } = err ? { match: null, candidates: [] } : matchBrand(f.name, shops);
            return {
                id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
                file: f,
                previewUrl: URL.createObjectURL(f),
                matchId: match?.id || null,
                candidates,
                ambiguous: !match && candidates.length > 0,
                size: f.size,
                error: err,
                status: "idle",
                message: null,
            };
        });
        setRows((prev) => [...prev, ...built]);
        if (inputRef.current) inputRef.current.value = "";
    };

    const removeRow = (id) => {
        setRows((prev) => {
            const gone = prev.find((r) => r.id === id);
            if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
            return prev.filter((r) => r.id !== id);
        });
    };

    const pickCandidate = (id, shopId) => {
        setRows((prev) =>
            prev.map((r) =>
                r.id === id ? { ...r, matchId: shopId || null, ambiguous: !shopId } : r,
            ),
        );
    };

    const runUploads = async () => {
        if (running) return;
        const queue = rows.filter((r) => r.matchId && !r.error);
        if (!queue.length) return;
        setRunning(true);
        setSummary(null);

        let ok = 0;
        let failed = 0;
        for (const r of queue) {
            setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status: "uploading", message: null } : x)));
            try {
                const fd = new FormData();
                fd.append("file", r.file);
                await api.post(`/admin/partner-shops/${r.matchId}/logo`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                ok += 1;
                setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status: "done" } : x)));
            } catch (e) {
                failed += 1;
                const msg = errorMessage(e);
                setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status: "failed", message: msg } : x)));
            }
        }

        const skipped = rows.length - queue.length;
        setSummary({ ok, failed, skipped });
        setRunning(false);
        if (ok > 0) toast.success(`${ok} logo${ok === 1 ? "" : "s"} uploaded`);
        onDone?.();
    };

    const handleClose = (nextOpen) => {
        if (!nextOpen && !running) {
            rows.forEach((r) => r.previewUrl && URL.revokeObjectURL(r.previewUrl));
            setRows([]);
            setSummary(null);
        }
        onOpenChange(nextOpen);
    };

    const [isDragOver, setIsDragOver] = useState(false);
    const onDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (running) return;
        addFiles(e.dataTransfer?.files);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                className="max-w-3xl"
                data-testid="bulk-logo-upload-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Upload size={16} className="text-orange-600" /> Bulk upload brand logos
                    </DialogTitle>
                    <DialogDescription>
                        Drop image files below. Filenames like <code>nesto.png</code>,{" "}
                        <code>kalyan-jewellers.webp</code> auto-match to a brand. Max{" "}
                        {MAX_FILES} files · {MAX_MB} MB each · PNG / JPG / WEBP.
                    </DialogDescription>
                </DialogHeader>

                {/* Dropzone */}
                <div
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => !running && inputRef.current?.click()}
                    className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-6 text-center transition ${isDragOver ? "border-orange-500 bg-orange-50" : "border-neutral-300 bg-neutral-50 hover:border-orange-400 hover:bg-orange-50/50"}`}
                    data-testid="bulk-logo-upload-dropzone"
                >
                    <Upload size={22} className="text-orange-600" />
                    <div className="text-sm font-semibold text-neutral-800">
                        Drop PNG / JPG / WEBP files here — filenames like <code>nesto.png</code> auto-match
                    </div>
                    <div className="text-xs text-neutral-500">or click to select</div>
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        onChange={(e) => addFiles(e.target.files)}
                    />
                </div>

                {/* Rows */}
                {rows.length > 0 && (
                    <div className="max-h-80 overflow-y-auto rounded-xl border border-neutral-200">
                        <table className="w-full text-left text-xs">
                            <thead className="sticky top-0 bg-neutral-50">
                                <tr className="border-b border-neutral-200 text-[10px] uppercase tracking-wide text-neutral-500">
                                    <th className="w-14 px-3 py-2">Preview</th>
                                    <th className="px-3 py-2">Filename</th>
                                    <th className="px-3 py-2">Matched brand</th>
                                    <th className="w-24 px-3 py-2">Status</th>
                                    <th className="w-10 px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const matched = r.matchId ? shopsById.get(r.matchId) : null;
                                    const willReplace = matched?.logo_url;
                                    return (
                                        <tr
                                            key={r.id}
                                            className="border-b border-neutral-100 last:border-none"
                                            data-testid={`bulk-logo-upload-row-${r.file.name}`}
                                        >
                                            <td className="px-3 py-2">
                                                <img
                                                    src={r.previewUrl}
                                                    alt=""
                                                    className="h-10 w-10 rounded-md object-cover ring-1 ring-neutral-200"
                                                />
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="truncate font-mono text-[11px] text-neutral-700" title={r.file.name}>
                                                    {r.file.name}
                                                </div>
                                                <div className="text-[10px] text-neutral-400">
                                                    {(r.size / 1024).toFixed(1)} KB
                                                </div>
                                                {r.error && (
                                                    <div className="mt-0.5 text-[10px] font-semibold text-rose-600">{r.error}</div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2">
                                                {r.error ? (
                                                    <span className="text-neutral-400">—</span>
                                                ) : r.ambiguous ? (
                                                    <div className="space-y-1">
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                                                            <AlertTriangle size={10} /> Ambiguous — pick one
                                                        </span>
                                                        <select
                                                            value={r.matchId || ""}
                                                            onChange={(e) => pickCandidate(r.id, e.target.value)}
                                                            className="block w-full rounded-md border border-neutral-300 px-2 py-1 text-[11px]"
                                                        >
                                                            <option value="">— Select brand —</option>
                                                            {r.candidates.map((c) => (
                                                                <option key={c.id} value={c.id}>{c.brand_name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : matched ? (
                                                    <div>
                                                        <div className="font-semibold text-neutral-800">{matched.brand_name}</div>
                                                        {willReplace && (
                                                            <div className="mt-0.5 text-[10px] font-semibold text-amber-700">
                                                                ⚠ Will replace existing logo
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                                                        <X size={10} /> No match
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-[11px]">
                                                {r.status === "idle" && <span className="text-neutral-400">Pending</span>}
                                                {r.status === "uploading" && (
                                                    <span className="inline-flex items-center gap-1 text-orange-600">
                                                        <Loader2 size={11} className="animate-spin" /> Uploading
                                                    </span>
                                                )}
                                                {r.status === "done" && (
                                                    <span className="inline-flex items-center gap-1 font-semibold text-emerald-600">
                                                        <CheckCircle2 size={12} /> Done
                                                    </span>
                                                )}
                                                {r.status === "failed" && (
                                                    <span
                                                        className="inline-flex items-center gap-1 text-rose-600"
                                                        title={r.message || "Failed"}
                                                    >
                                                        <AlertTriangle size={11} /> Failed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {!running && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeRow(r.id)}
                                                        aria-label={`Remove ${r.file.name}`}
                                                        className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-rose-600"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {summary && (
                    <div
                        className="rounded-xl bg-neutral-50 px-3 py-2 text-xs text-neutral-700"
                        data-testid="bulk-logo-upload-summary"
                    >
                        <span className="font-semibold text-emerald-700">Uploaded {summary.ok}</span>
                        {" · "}
                        <span className={summary.failed ? "font-semibold text-rose-700" : ""}>
                            Failed {summary.failed}
                        </span>
                        {" · "}
                        <span className={summary.skipped ? "font-semibold text-amber-700" : ""}>
                            Skipped {summary.skipped} (unmatched)
                        </span>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleClose(false)}
                        disabled={running}
                    >
                        {summary ? "Close" : "Cancel"}
                    </Button>
                    <Button
                        type="button"
                        onClick={runUploads}
                        disabled={running || matchedCount === 0}
                        className="nest-gradient text-white"
                        data-testid="bulk-logo-upload-confirm-btn"
                    >
                        {running ? (
                            <><Loader2 size={14} className="mr-1 animate-spin" /> Uploading…</>
                        ) : (
                            <><Upload size={14} className="mr-1" /> Upload {matchedCount} matched file{matchedCount === 1 ? "" : "s"}</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
